// ─── Document viewer store ──────────────────────────────────────────────────────
//
// A tiny pub/sub holding the document currently open in the split panel. Attachment
// cards (in the chat) call openDocument(ref); the DocumentPanel reads the current ref
// and AppShell listens to bring the panel on-screen. Kept OUTSIDE React so a card
// rendered deep inside renderContent / a tool-call turn can open a doc without any
// prop-drilling — same spirit as panels/index.ts being a flat registry.
//
// Three kinds of reference, matching how an agent can "send" a Markdown file:
//   • file   — a .md path on disk; the panel fetches it via /api/chats/:id/file
//   • url    — a remote .md link; the panel fetches it via /api/chats/:id/fetch-md
//   • inline — Markdown the agent printed straight into the chat (a ```markdown block);
//              the content travels WITH the ref, so the panel needs no fetch.

export interface DocRef {
  kind:     'file' | 'url' | 'inline'
  /** Display name shown on the card + panel header (e.g. "report.md"). */
  name:     string
  /** file: the path · url: the URL. Absent for inline docs. */
  src?:     string
  /** inline only: the Markdown text itself. */
  content?: string
  /** Chat this doc belongs to — needed to scope the fetch to the right cwd. */
  chatId?:  string
}

let current: DocRef | null = null
const listeners = new Set<() => void>()

/** Open a document in the split panel (last opened wins). */
export function openDocument(ref: DocRef): void {
  current = ref
  for (const l of listeners) l()
}

/** The document currently open, or null. */
export function getCurrentDocument(): DocRef | null {
  return current
}

/** Subscribe to open-document changes; returns an unsubscribe fn (useSyncExternalStore-shaped). */
export function subscribeDocument(fn: () => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

/** Stable identity for a ref — used to dedupe cards and as a panel render key. */
export function docKey(ref: DocRef): string {
  return `${ref.kind}:${ref.src ?? ref.name}`
}
